/**
 * StrainProfitabilityTable Component
 *
 * Displays a sortable table of strain-level profitability data.
 * Columns: Strain, Revenue, kg Sold, Avg Price/kg, Cost/kg, Contribution, Margin %
 *
 * Part of US-P7-002 Step 6: UI Components
 */

import { useState, useMemo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowUpDown, ArrowUp, ArrowDown, Info } from 'lucide-react'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip'
import MarginBadge from './MarginBadge'

/**
 * Format a currency value in British pounds
 *
 * @param {number|null} value - Value to format
 * @returns {string} Formatted currency string
 */
function formatCurrency(value) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

/**
 * Format a kg value with two decimal places
 *
 * @param {number|null} value - Value to format
 * @returns {string} Formatted kg string
 */
function formatKg(value) {
  if (value === null || value === undefined) return '-'
  return value.toFixed(2)
}

/**
 * Column definitions for the table
 */
const columns = [
  { key: 'strain_name', label: 'Strain', sortable: true },
  { key: 'revenue', label: 'Revenue', sortable: true, tip: 'Total sales income for this strain (quantity × price)' },
  { key: 'kg_sold', label: 'kg Sold', sortable: true, tip: 'Total weight sold in kilograms' },
  { key: 'avg_price_per_kg', label: 'Avg Price/kg', sortable: true, tip: 'Average selling price per kilogram (revenue ÷ kg sold)' },
  { key: 'variable_cost_per_kg', label: 'Cost/kg', sortable: true, tip: 'Production cost per kg — substrate, spawn, and energy. Does not include fixed costs like rent or labour' },
  { key: 'contribution_margin', label: 'Contribution', sortable: true, tip: 'Revenue minus variable production costs. The amount this strain contributes toward covering fixed costs' },
  { key: 'margin_percent', label: 'Margin %', sortable: true, tip: 'Contribution as a percentage of revenue. Higher is better — but fixed costs still need to be covered' },
]

/**
 * Sort data by column
 *
 * @param {Array} data - Data to sort
 * @param {string} sortKey - Column key to sort by
 * @param {string} sortDirection - 'asc' or 'desc'
 * @returns {Array} Sorted data
 */
function sortData(data, sortKey, sortDirection) {
  return [...data].sort((a, b) => {
    const aVal = a[sortKey]
    const bVal = b[sortKey]

    // Handle null values - sort them to the end
    if (aVal === null || aVal === undefined) return 1
    if (bVal === null || bVal === undefined) return -1

    // String comparison for strain_name
    if (sortKey === 'strain_name') {
      const comparison = aVal.localeCompare(bVal)
      return sortDirection === 'asc' ? comparison : -comparison
    }

    // Numeric comparison for other columns
    const comparison = aVal - bVal
    return sortDirection === 'asc' ? comparison : -comparison
  })
}

/**
 * StrainProfitabilityTable Component
 *
 * @param {Object} props
 * @param {Array} props.data - Array of strain profitability data
 * @param {boolean} [props.loading=false] - Loading state
 * @param {Function} [props.onRowClick] - Row click handler, receives strain_id
 * @param {string} [props.className] - Additional CSS classes
 */
export default function StrainProfitabilityTable({
  data,
  loading = false,
  onRowClick,
  className = ''
}) {
  // Default sort by revenue descending
  const [sortKey, setSortKey] = useState('revenue')
  const [sortDirection, setSortDirection] = useState('desc')

  // Sort the data
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return []
    return sortData(data, sortKey, sortDirection)
  }, [data, sortKey, sortDirection])

  // Compute totals
  const totals = useMemo(() => {
    if (!data || data.length === 0) return null
    const revenue = data.reduce((s, r) => s + (r.revenue || 0), 0)
    const kgSold = data.reduce((s, r) => s + (r.kg_sold || 0), 0)
    const contribution = data.reduce((s, r) => s + (r.contribution_margin || 0), 0)
    return {
      revenue,
      kgSold,
      avgPricePerKg: kgSold > 0 ? revenue / kgSold : 0,
      contribution,
      marginPercent: revenue > 0 ? (contribution / revenue) * 100 : 0
    }
  }, [data])

  /**
   * Handle column header click for sorting
   */
  const handleSort = (key) => {
    if (sortKey === key) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      // New column, default to descending
      setSortKey(key)
      setSortDirection('desc')
    }
  }

  /**
   * Get the sort icon for a column
   */
  const getSortIcon = (key) => {
    if (sortKey !== key) {
      return <ArrowUpDown className="h-4 w-4 ml-1" />
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />
  }

  /**
   * Handle row click
   */
  const handleRowClick = (strainId) => {
    if (onRowClick) {
      onRowClick(strainId)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className={className}>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className={className}>
        <div className="text-center py-8 text-neutral-500">
          No strain profitability data available for this period
        </div>
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={300}>
    <div className={className}>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead key={column.key}>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleSort(column.key)}
                    className="flex items-center hover:text-neutral-900 transition-colors"
                  >
                    {column.label}
                    {getSortIcon(column.key)}
                  </button>
                  {column.tip && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 text-muted-foreground/50 hover:text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-xs">
                        {column.tip}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData.map((row) => (
            <TableRow
              key={row.strain_id}
              onClick={() => handleRowClick(row.strain_id)}
              className={onRowClick ? 'cursor-pointer hover:bg-neutral-50' : ''}
            >
              <TableCell className="font-medium">{row.strain_name}</TableCell>
              <TableCell>{formatCurrency(row.revenue)}</TableCell>
              <TableCell>{formatKg(row.kg_sold)}</TableCell>
              <TableCell>{formatCurrency(row.avg_price_per_kg)}</TableCell>
              <TableCell>{formatCurrency(row.variable_cost_per_kg)}</TableCell>
              <TableCell>{formatCurrency(row.contribution_margin)}</TableCell>
              <TableCell>
                <MarginBadge margin={row.margin_percent} size="compact" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        {totals && (
          <TableFooter>
            <TableRow>
              <TableCell className="font-semibold">Total</TableCell>
              <TableCell className="font-semibold">{formatCurrency(totals.revenue)}</TableCell>
              <TableCell className="font-semibold">{formatKg(totals.kgSold)}</TableCell>
              <TableCell className="font-semibold">{formatCurrency(totals.avgPricePerKg)}</TableCell>
              <TableCell></TableCell>
              <TableCell className="font-semibold">{formatCurrency(totals.contribution)}</TableCell>
              <TableCell>
                <MarginBadge margin={totals.marginPercent} size="compact" />
              </TableCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </div>
    </TooltipProvider>
  )
}
